'use client';

import React, { useContext, useMemo } from 'react';
import Link from 'next/link';
import { ScreenSizeContext, UserTypeContext } from '@context';
import { msalService } from '@services';
import { WaterIQUserType } from '@types';
import { cn, useLogout } from '@utils';

import { CONSTANT } from '@/constants';
import { GlobalNav, Logout, QGovLogoLight, QGovLogoStacked } from '../icons';
import { MobileNav } from '../navigation';
import { Container } from './Container';
import { NavLink } from './NavLink';

export const Header = () => {
	const logout = useLogout();
	// Context
	const { setSelectedUserType } = useContext(UserTypeContext);
	const { isMdScreen } = useContext(ScreenSizeContext);
	// Memo
	const availableUserTypes = useMemo(() => msalService.getUserTypes(), []);

	return (
		<header>
			{/* Pre-header section with logo and links */}
			<div className="bg-dark-background">
				<Container className="flex h-12 items-center justify-between pr-0 sm:pr-0 md:h-18 md:pr-0 lg:h-9 lg:px-12">
					{/* Government websites link */}
					<NavLink
						href={CONSTANT.QLD_GOV_URL}
						theme="dark"
						className="text-xs whitespace-nowrap no-underline hover:underline"
						leftIcon={
							isMdScreen ? (
								<QGovLogoLight className="md:h-10 md:w-59" />
							) : (
								<GlobalNav className="text-dark-action-secondary size-icon-sm" />
							)
						}
					>
						{!isMdScreen && 'Queensland Government websites'}
					</NavLink>

					{/* Right aligned links in the preheader */}
					{!isMdScreen && (
						<div className="flex space-x-4">
							{(availableUserTypes ?? []).length > 1 && (
								<NavLink
									href="#"
									theme="dark"
									onClick={() => setSelectedUserType(WaterIQUserType.WEH)}
									className="text-xs whitespace-nowrap no-underline hover:underline"
									leftIcon={<Logout className="text-dark-action-secondary size-icon-sm" />}
								>
									Switch to WEH portal
								</NavLink>
							)}
							<NavLink
								href="#"
								theme="dark"
								onClick={logout}
								className="text-xs whitespace-nowrap no-underline hover:underline"
								leftIcon={<Logout className="text-dark-action-secondary size-icon-sm" />}
							>
								Log Out
							</NavLink>
						</div>
					)}

					{/* Main navigation controls (on mobile and tablet screens) */}
					{isMdScreen && (
						<div className="pl-2">
							<MobileNav />
						</div>
					)}
				</Container>
			</div>

			{/* Main header section */}
			<div className="bg-neutral-white border-dark-design-accent border-b-4 lg:border-b-0">
				<Container className="flex items-center justify-between py-4 md:py-6 lg:py-8">
					{/* Header brand section */}
					<Link href={CONSTANT.PORTAL_URL} className="group flex items-center underline-offset-3">
						{/* QLD Government logo */}
						{!isMdScreen && (
							<div className="border-dark-design-accent mr-6 border-r-2 pr-6">
								<QGovLogoStacked className="text-neutral-black group-hover:text-light-link" />
							</div>
						)}
						{/* Site name */}
						<h1
							className={cn(
								'decoration-light-link text-sm leading-5 font-bold decoration-2',
								'md:text-md group-hover:text-light-link group-hover:underline',
							)}
						>
							WaterIQ
						</h1>
					</Link>
				</Container>
			</div>
		</header>
	);
};
